import { NextPage } from 'next';
import Head from 'next/head';
import { Box, Container, Typography, AppBar, Toolbar } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GetStaticProps } from 'next';
import fs from 'fs';
import path from 'path';

interface RaffleTermsPageProps {
  termsContent: string;
}

const RaffleTermsPage: NextPage<RaffleTermsPageProps> = ({ termsContent }) => {
  return (
    <>
      <Head>
        <title>Raffle & Sweepstakes Terms - Zenlit</title>
        <meta name="description" content="Zenlit Raffle and Sweepstakes Terms" />
        <link rel="icon" href="/images/logo-coin.png" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap"
        />
      </Head>

      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'white',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
          <Toolbar sx={{ justifyContent: 'space-between', py: 1, maxWidth: '1920px', mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src="/images/logo-coin.png"
                alt="Zenlit Logo"
                sx={{
                  width: { xs: 40, md: 58 },
                  height: { xs: 40, md: 58 },
                }}
              />
              <Typography
                variant="h4"
                component="div"
                sx={{
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                  fontWeight: 700,
                  color: '#6C5DD3',
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                Zenlit
              </Typography>
            </Box>
          </Toolbar>
        </Box>
      </AppBar>

      <Box sx={{ py: 10, backgroundColor: 'white', minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 3,
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1)',
              p: { xs: 3, md: 6 },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2rem', md: '2.9rem' },
                fontWeight: 700,
                color: '#6C5DD3',
                mb: 4,
                textAlign: 'center',
                fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                lineHeight: 1.1,
              }}
            >
              Raffle & Sweepstakes Terms
            </Typography>

            <Box
              sx={{
                '& h1': {
                  fontSize: '1.8rem',
                  fontWeight: 700,
                  color: '#1F2937',
                  mb: 2,
                  mt: 4,
                  borderBottom: '2px solid #6C5DD3',
                  paddingBottom: '8px',
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                },
                '& h2': {
                  fontSize: '1.6rem',
                  fontWeight: 600,
                  color: '#1F2937',
                  mb: 2,
                  mt: 3,
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                },
                '& h3': {
                  fontSize: '1.4rem',
                  fontWeight: 600,
                  color: '#1F2937',
                  mb: 2,
                  mt: 3,
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                },
                '& h4': {
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  color: '#1F2937',
                  mb: 2,
                  mt: 3,
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                },
                '& h5': {
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#1F2937',
                  mb: 2,
                  mt: 3,
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                },
                '& h6': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#1F2937',
                  mb: 2,
                  mt: 3,
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                },
                '& p': {
                  color: '#1F2937',
                  lineHeight: 1.6,
                  mb: 1,
                  fontSize: '1rem',
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                },
                '& ul, & ol': {
                  color: '#1F2937',
                  mb: 2,
                  pl: 3,
                },
                '& li': {
                  mb: 1,
                  lineHeight: 1.6,
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                },
                '& strong, & b': {
                  fontWeight: 600,
                  color: '#1F2937',
                  marginBottom: '4px',
                  display: 'inline-block',
                },
                '& em, & i': {
                  fontStyle: 'italic',
                },
                '& a': {
                  color: '#6C5DD3',
                  textDecoration: 'underline',
                  '&:hover': {
                    color: '#5A4BC4',
                  },
                },
                '& blockquote': {
                  paddingLeft: '16px',
                  margin: '16px 0',
                  fontStyle: 'italic',
                  color: '#1F2937',
                  padding: '16px',
                },
                '& code': {
                  backgroundColor: '#F3F4F6',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                  color: '#1F2937',
                },
                '& pre': {
                  backgroundColor: '#F9FAFB',
                  padding: '16px',
                  borderRadius: '8px',
                  overflow: 'auto',
                  margin: '16px 0',
                  border: '1px solid #E5E7EB',
                },
                '& pre code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 3,
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  overflow: 'hidden',
                },
                '& th, & td': {
                  border: '1px solid #E5E7EB',
                  padding: '12px',
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: '#6C5DD3',
                  fontWeight: 600,
                  color: 'white',
                },
                '& hr': {
                  border: 'none',
                  borderTop: '2px solid #6C5DD3',
                  margin: '24px 0',
                },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{termsContent}</ReactMarkdown>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export const getStaticProps: GetStaticProps<RaffleTermsPageProps> = async () => {
  try {
    const termsPath = path.join(process.cwd(), 'public', 'terms', 'raffleterms.md');

    if (!fs.existsSync(termsPath)) {
      console.warn('raffleterms.md not found, using empty content');
      return {
        props: {
          termsContent: '# Raffle & Sweepstakes Terms\n\nContent not available.',
        },
      };
    }

    const termsContent = fs.readFileSync(termsPath, 'utf-8');

    return {
      props: {
        termsContent,
      },
    };
  } catch (error) {
    console.error('Error reading raffle terms content:', error);
    return {
      props: {
        termsContent: '# Raffle & Sweepstakes Terms\n\nError loading content.',
      },
    };
  }
};

export default RaffleTermsPage;
